import React, { useState, useEffect, useRef } from "react";
import { Link } from "gatsby";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import BookingLeadForm from "@/components/BookingLeadForm";
import {
  Check,
  ChevronDown,
  Phone,
  Calendar,
  Shield,
  CreditCard,
  Heart,
  Star,
  Zap,
  FileText,
  Baby,
  Instagram,
} from "lucide-react";

// ─── Schema.org structured data ───────────────────────────────────────────────
const pricingSchema = {
  "@context": "https://schema.org",
  "@type": "Dentist",
  name: "Y3 Smiles Dental",
  url: "https://y3smilesdental.com.au",
  telephone: "+61390224442",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Unit 1, 34 King William Street",
    addressLocality: "Broadmeadows",
    addressRegion: "VIC",
    postalCode: "3047",
    addressCountry: "AU",
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "17:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: "Saturday",
      opens: "09:00",
      closes: "13:00",
    },
  ],
  priceRange: "$$",
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Dental Treatment Price Guide",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: { "@type": "MedicalProcedure", name: "New Patient Check-Up & Clean" },
        price: "229",
        priceCurrency: "AUD",
      },
      {
        "@type": "Offer",
        itemOffered: { "@type": "MedicalProcedure", name: "Tooth-Coloured Filling" },
        price: "200",
        priceCurrency: "AUD",
      },
    ],
  },
};

// ─── Color tokens — extracted from y3smilesdental.com.au site palette ──────────
// #B59682 = .bg-booking-gold (index.css:401) — the header BOOK button / primary CTA
// #A4846F = hover state (button.tsx line 27)
const C = {
  navy: "#1A1A1A",   // site charcoal — heading/nav/dark sections
  teal: "#B59682",   // site primary brand gold (≡ --primary, booking-gold)
  amber: "#E59B20",  // badge accent only
  warmWhite: "#FFFFFF",
  warmGrey: "#F8F8F8",
  white: "#FFFFFF",
};

// ─── Types ─────────────────────────────────────────────────────────────────────
interface PriceItem {
  name: string;
  price: string;
  description: string;
  badge?: "popular" | "cdbs" | "interest-free";
  badgeLabel?: string;
  included?: string[];
  costFactors?: string;
  insuranceNote?: string;
}

interface Category {
  id: string;
  label: string;
  items: PriceItem[];
}

// ─── Price data ────────────────────────────────────────────────────────────────
const categories: Category[] = [
  {
    id: "checkups",
    label: "Check-Ups & Cleans",
    items: [
      {
        name: "Limited Tooth Examination",
        price: "From $65",
        description: "Quick assessment for a specific area of concern.",
        included: ["Targeted examination by a registered dentist"],
        costFactors: "Complexity of the concern being assessed.",
        insuranceNote: "May be partially covered depending on your fund and level of cover.",
      },
      {
        name: "New Patient Check-Up & Clean (with X-rays)",
        price: "$229",
        description: "Includes check-up, X-rays (if needed), photos, professional clean.",
        badge: "popular",
        badgeLabel: "Most Popular",
        included: [
          "Comprehensive oral examination",
          "Digital X-rays (if clinically indicated)",
          "Intraoral photos",
          "Professional scale and clean",
        ],
        costFactors: "X-rays not always required — cost may adjust if omitted at clinician discretion.",
        insuranceNote: "Many private health patients pay $0 out-of-pocket. HICAPS claiming on-site.",
      },
      {
        name: "New Patient Check-Up & Clean (no X-rays)",
        price: "$199",
        description: "Conditions apply. No X-rays included.",
        included: ["Oral examination", "Professional scale and clean"],
        insuranceNote: "Check your fund for rebate details.",
      },
      {
        name: "Children's Check-Up & Clean (Age 0–6)",
        price: "From $99",
        description: "Without CDBS or private health insurance.",
        costFactors: "Age and complexity of treatment required.",
      },
      {
        name: "Children's Check-Up & Clean (Age 7–15)",
        price: "From $149",
        description: "Without CDBS or private health insurance.",
        costFactors: "Age and complexity of treatment required.",
      },
      {
        name: "Children's Dental (CDBS Eligible)",
        price: "$0",
        description: "Free for eligible children aged 1–17 via Medicare CDBS.",
        badge: "cdbs",
        badgeLabel: "Medicare Bulk Billed",
        included: [
          "Examination",
          "X-rays (if required)",
          "Professional cleaning",
          "Fissure sealants (where indicated)",
          "Fluoride treatment",
        ],
        insuranceNote: "Eligibility determined by Medicare. Up to $1,158 over two calendar years. Bring your Medicare card. We'll check at no charge.",
      },
      {
        name: "Existing Patient Check-Up & Clean",
        price: "$199",
        description: "Recommended every 6 months to maintain your oral health.",
        included: ["Oral examination", "Scale and clean", "X-rays if clinically required"],
        insuranceNote: "Most private health funds contribute significantly. HICAPS on-site.",
      },
    ],
  },
  {
    id: "fillings",
    label: "Fillings & Restorations",
    items: [
      {
        name: "Small Tooth-Coloured Filling",
        price: "From $200",
        description: "Tooth-coloured composite material that blends naturally with your smile.",
        included: [
          "Dental examination",
          "Composite resin filling",
          "Shade matching for natural appearance",
        ],
        costFactors: "Size of the cavity, number of surfaces affected, and complexity of the restoration.",
        insuranceNote: "Private health cover may contribute depending on your fund.",
      },
      {
        name: "Larger Restoration or Rebuild",
        price: "From $300",
        description: "For teeth with more extensive decay or damage.",
        costFactors: "Exact cost confirmed after clinical assessment — varies based on extent of damage.",
        insuranceNote: "Discuss with your dentist and health fund before treatment.",
      },
    ],
  },
  {
    id: "extractions",
    label: "Extractions",
    items: [
      {
        name: "Simple Tooth Extraction",
        price: "From $250",
        description: "Includes consultation and X-ray where required.",
        included: [
          "Pre-extraction examination",
          "Local anaesthetic",
          "Extraction",
          "Post-operative instructions",
        ],
        costFactors: "Tooth position, root anatomy, and whether sedation is required.",
        insuranceNote: "Private health cover generally applies. Check your extras cover.",
      },
      {
        name: "Complex Tooth Extraction",
        price: "Assessed individually",
        description: "Exact cost confirmed after clinical assessment.",
        costFactors: "Complexity varies widely. Includes surgical extractions of broken or impacted teeth.",
        insuranceNote: "Obtain a written estimate before proceeding.",
      },
      {
        name: "Wisdom Tooth Extraction",
        price: "From $350",
        description: "For straightforward wisdom tooth removal.",
        costFactors: "Impacted wisdom teeth or those requiring surgical techniques will cost more.",
        insuranceNote: "Private health extras generally apply.",
      },
    ],
  },
  {
    id: "cosmetic",
    label: "Cosmetic Dentistry",
    items: [
      {
        name: "Take-Home Teeth Whitening Kit",
        price: "From $299",
        description: "Custom-fitted whitening trays with professional-grade gel.",
        included: ["Custom impression and trays", "Whitening gel", "Instructions and take-home kit"],
        costFactors: "Kit inclusions may vary. Discuss with your dentist.",
      },
      {
        name: "In-Chair Teeth Whitening",
        price: "$499",
        description: "Professional whitening treatment with same-day results.",
        included: [
          "Pre-whitening examination",
          "In-chair whitening session",
          "Post-treatment care instructions",
        ],
        costFactors: "Number of sessions required may vary.",
      },
      {
        name: "Night Splint (Teeth Grinding Guard)",
        price: "$650",
        description: "Custom-made splint to protect your teeth from bruxism damage.",
        included: [
          "Clinical examination",
          "Impressions",
          "Custom-fabricated night splint",
          "Fitting appointment",
        ],
        costFactors: "Type of splint and material selected.",
      },
    ],
  },
  {
    id: "orthodontics",
    label: "Orthodontics",
    items: [
      {
        name: "Braces & Clear Aligners",
        price: "Varies by complexity",
        description: "Orthodontic treatment to straighten teeth and improve your bite. Consultation required for a personalised quote.",
        badge: "interest-free",
        badgeLabel: "Interest-Free Available",
        costFactors: "Severity of misalignment, treatment duration, and type of appliance selected.",
        insuranceNote: "Some orthodontic cover may apply under private health extras.",
      },
    ],
  },
  {
    id: "implants",
    label: "Implants, Crowns & Bridges",
    items: [
      {
        name: "Dental Implants",
        price: "From $4,000",
        description: "A long-term, natural-looking replacement for a missing tooth. Varies by complexity.",
        badge: "interest-free",
        badgeLabel: "Interest-Free Available",
        included: [
          "Comprehensive examination and X-rays",
          "Implant placement",
          "Crown restoration",
        ],
        costFactors: "Number of implants, bone condition, need for bone grafting, and type of restoration.",
        insuranceNote: "Some major dental extras cover may apply. Confirm with your fund.",
      },
      {
        name: "Dental Bridge",
        price: "From $2,800",
        description: "Replaces one or more missing teeth, anchored to adjacent teeth. Exact cost after assessment.",
        costFactors: "Number of teeth being replaced, material choice, and preparation of abutment teeth.",
        insuranceNote: "Major dental cover may apply. Obtain a written estimate before proceeding.",
      },
      {
        name: "Dental Crowns",
        price: "From $1,600",
        description: "Protects and restores a damaged tooth. Shade and material confirmed at consultation.",
        included: [
          "Examination and shade selection",
          "Tooth preparation",
          "Laboratory-fabricated crown",
          "Fitting appointment",
        ],
        costFactors: "Material choice (ceramic, porcelain, zirconia) and number of surfaces.",
        insuranceNote: "Major dental cover may apply depending on your fund.",
      },
      {
        name: "Root Canal Treatment",
        price: "From $1,100",
        description: "All-inclusive. Saves the natural tooth by treating infection.",
        included: [
          "Examination and X-rays",
          "Canal treatment across appointments",
          "Temporary and permanent restoration",
        ],
        costFactors: "Number of canals in the tooth (molars have more canals than incisors).",
        insuranceNote: "Major dental extras cover typically applies. Confirm with your fund.",
      },
    ],
  },
  {
    id: "removable",
    label: "Removable Appliances",
    items: [
      {
        name: "Dentures",
        price: "From $1,200",
        description: "Removable tooth replacement. Pricing varies by type and complexity.",
        included: [
          "Consultation and impressions",
          "Trial fitting",
          "Final denture fitting",
          "Adjustments",
        ],
        costFactors: "Full vs partial dentures, number of teeth, and materials used.",
        insuranceNote: "Major dental cover may apply. Check your fund.",
      },
    ],
  },
  {
    id: "children",
    label: "Children's Dentistry",
    items: [
      {
        name: "Children's Dental (CDBS Eligible)",
        price: "$0",
        description: "Free dental care for eligible children aged 1–17 via Medicare.",
        badge: "cdbs",
        badgeLabel: "Medicare Bulk Billed",
        included: [
          "Examination",
          "X-rays (if required)",
          "Professional cleaning",
          "Fissure sealants (where indicated)",
          "Fluoride treatment",
        ],
        insuranceNote: "Eligibility determined by Medicare. Up to $1,158 over two calendar years. Bring Medicare card.",
      },
    ],
  },
];

// ─── Sub-components ────────────────────────────────────────────────────────────
function TrustBadge({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        background: "rgba(255,255,255,0.1)",
        border: "1px solid rgba(255,255,255,0.2)",
        borderRadius: "8px",
        padding: "10px 14px",
        color: C.warmWhite,
        fontSize: "0.875rem",
        fontWeight: 500,
      }}
    >
      <span style={{ color: C.teal, flexShrink: 0 }}>{icon}</span>
      {label}
    </div>
  );
}

function BadgePill({ type, label }: { type: PriceItem["badge"]; label?: string }) {
  if (!type) return null;
  const bg = type === "cdbs" ? C.teal : type === "interest-free" ? C.navy : C.amber;
  const textColor = type === "cdbs" ? C.navy : C.white;
  return (
    <span
      style={{
        background: bg,
        color: textColor,
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase" as const,
        padding: "3px 10px",
        borderRadius: "999px",
        display: "inline-block",
        marginBottom: "8px",
      }}
    >
      {label}
    </span>
  );
}

function PriceCard({ item, onBookClick }: { item: PriceItem; onBookClick: (treatment: string) => void }) {
  const [open, setOpen] = useState(false);
  const hasDetails = !!(item.included || item.costFactors || item.insuranceNote);

  return (
    <div
      style={{
        background: C.white,
        borderRadius: "12px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
        border: "1px solid rgba(11,31,58,0.06)",
        display: "flex",
        flexDirection: "column" as const,
      }}
    >
      <div style={{ padding: "20px 20px 0" }}>
        <BadgePill type={item.badge} label={item.badgeLabel} />
        <h3 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: "1.05rem", color: C.navy, margin: "0 0 6px", lineHeight: 1.35 }}>
          {item.name}
        </h3>
        {item.description && (
          <p style={{ color: "#555", fontSize: "0.875rem", margin: 0, lineHeight: 1.6 }}>{item.description}</p>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", gap: "12px", marginTop: "auto" }}>
        <span style={{ color: C.navy, fontWeight: 700, fontSize: "1.3rem", fontFamily: "'DM Serif Display', Georgia, serif" }}>
          {item.price}
        </span>
        <button
          onClick={() => {
            onBookClick(item.name);
            if (typeof window !== "undefined" && (window as any).dataLayer) {
              (window as any).dataLayer.push({ event: "cta_clicked", button_label: "Book This", treatment_name: item.name, position: "price_card" });
            }
          }}
          style={{
            background: "transparent",
            border: `2px solid ${C.teal}`,
            color: C.teal,
            borderRadius: "6px",
            padding: "8px 16px",
            fontSize: "0.875rem",
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap" as const,
          }}
          aria-label={`Book treatment: ${item.name}`}
        >
          Book This →
        </button>
      </div>

      {hasDetails && (
        <>
          <div style={{ borderTop: "1px solid rgba(11,31,58,0.06)", margin: "0 20px" }} />
          <button
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#777",
              fontSize: "0.8rem",
              padding: "10px 20px",
              width: "100%",
              textAlign: "left" as const,
            }}
          >
            <ChevronDown size={14} style={{ transition: "transform 0.25s", transform: open ? "rotate(180deg)" : "none" }} />
            {open ? "Hide details" : "See what's included ▾"}
          </button>
          {open && (
            <div style={{ padding: "0 20px 20px" }} aria-live="polite">
              {item.included && item.included.length > 0 && (
                <div style={{ marginBottom: "12px" }}>
                  <p style={{ fontWeight: 600, fontSize: "0.8rem", color: C.navy, marginBottom: "6px" }}>What's included</p>
                  <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                    {item.included.map((inc, i) => (
                      <li key={i} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.82rem", color: "#444", marginBottom: "4px" }}>
                        <Check size={12} color={C.teal} />
                        {inc}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {item.costFactors && (
                <div style={{ marginBottom: "8px" }}>
                  <p style={{ fontWeight: 600, fontSize: "0.8rem", color: C.navy, marginBottom: "4px" }}>What may affect cost</p>
                  <p style={{ fontSize: "0.82rem", color: "#666", margin: 0 }}>{item.costFactors}</p>
                </div>
              )}
              {item.insuranceNote && (
                <div style={{ marginBottom: "8px" }}>
                  <p style={{ fontWeight: 600, fontSize: "0.8rem", color: C.navy, marginBottom: "4px" }}>Insurance</p>
                  <p style={{ fontSize: "0.82rem", color: "#666", margin: 0 }}>{item.insuranceNote}</p>
                </div>
              )}
              <p style={{ fontSize: "0.75rem", color: "#999", margin: "12px 0 0", fontStyle: "italic" }}>
                Cost confirmed after clinical assessment by a registered dentist.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FaqItem({ q, a }: { q: string; a: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid rgba(11,31,58,0.1)" }}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "20px 0",
          textAlign: "left" as const,
          gap: "16px",
        }}
      >
        <span style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: "1rem", color: C.navy, lineHeight: 1.4 }}>
          {q}
        </span>
        <ChevronDown size={18} color={C.teal} style={{ flexShrink: 0, transition: "transform 0.25s", transform: open ? "rotate(180deg)" : "none" }} />
      </button>
      {open && (
        <div style={{ paddingBottom: "20px", color: "#444", fontSize: "0.9rem", lineHeight: 1.75 }} aria-live="polite">
          {a}
        </div>
      )}
    </div>
  );
}

// ─── Main page component ───────────────────────────────────────────────────────
const PricingPage = () => {
  const [activeCategory, setActiveCategory] = useState("checkups");
  const [selectedTreatment, setSelectedTreatment] = useState("");
  const [showStickyBar, setShowStickyBar] = useState(true);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window !== "undefined" && (window as any).dataLayer) {
      (window as any).dataLayer.push({ event: "pricing_page_view" });
    }
  }, []);

  useEffect(() => {
    const el = formRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => setShowStickyBar(!entry.isIntersecting), { threshold: 0.2 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const gallery = document.getElementById("clinic-gallery");
    if (!gallery) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && typeof window !== "undefined" && (window as any).dataLayer) {
          (window as any).dataLayer.push({ event: "clinic_photo_viewed" });
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(gallery);
    return () => obs.disconnect();
  }, []);

  const handleBookClick = (treatmentName: string) => {
    setSelectedTreatment(treatmentName);
    if (typeof document !== "undefined") {
      const el = document.getElementById("book");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handlePhoneTap = (source: string) => {
    if (typeof window !== "undefined" && (window as any).dataLayer) {
      (window as any).dataLayer.push({ event: "phone_tap", click_source: source });
    }
  };

  const handleCtaClick = (label: string, position: string) => {
    if (typeof window !== "undefined" && (window as any).dataLayer) {
      (window as any).dataLayer.push({ event: "cta_clicked", button_label: label, position });
    }
  };

  const activeItems = categories.find((c) => c.id === activeCategory)?.items ?? [];

  const serifHeading: React.CSSProperties = { fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 };
  const pad: React.CSSProperties = { padding: "72px 0" };
  const wrap: React.CSSProperties = { maxWidth: "860px", margin: "0 auto", padding: "0 20px" };

  const tealBtn = (size: "md" | "lg" = "md"): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: C.teal,
    color: C.white,
    borderRadius: "8px",
    padding: size === "lg" ? "16px 32px" : "12px 24px",
    fontSize: size === "lg" ? "1rem" : "0.9rem",
    fontWeight: 700,
    border: "none",
    cursor: "pointer",
    textDecoration: "none",
  });

  const outlineBtn = (size: "md" | "lg" = "md"): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: "transparent",
    color: C.white,
    borderRadius: "8px",
    padding: size === "lg" ? "16px 32px" : "12px 24px",
    fontSize: size === "lg" ? "1rem" : "0.9rem",
    fontWeight: 700,
    border: `2px solid ${C.white}`,
    cursor: "pointer",
    textDecoration: "none",
  });

  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: "Inter, system-ui, sans-serif", lineHeight: 1.7, background: C.warmWhite }}>
      <SEO
        title="Dental Prices Broadmeadows | Y3 Smiles Dental – Transparent Cost Guide"
        description="Clear dental prices in Broadmeadows. Check-up & clean from $199. CDBS bulk billing for eligible kids. Flexible payment plans. No surprise fees. Book online today."
        canonicalUrl="https://y3smilesdental.com.au/patient-resources/pricing"
        image="/og-reception.jpg"
      >
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&display=swap"
          rel="stylesheet"
        />
        <meta
          name="keywords"
          content="dentist Broadmeadows prices, dental cost Melbourne north, CDBS dentist Broadmeadows, cheap dentist Glenroy, dental payment plan northern suburbs"
        />
        <meta property="og:title" content="Dental Prices Broadmeadows – Y3 Smiles Dental" />
        <meta
          property="og:description"
          content="Transparent pricing. Flexible payments. CDBS bulk billing. See our clear price guide and book online."
        />
        <meta property="og:url" content="https://y3smilesdental.com.au/patient-resources/pricing" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(pricingSchema)}</script>
      </SEO>
      <Header />
      <main className="flex-grow" style={{ paddingTop: "80px" }}>

        {/* HERO */}
        <section style={{ background: C.navy, padding: "80px 0 64px" }}>
          <div style={wrap}>
            <div style={{ textAlign: "center" }}>
              <h1 style={{ ...serifHeading, fontSize: "clamp(1.8rem, 5vw, 2.8rem)", color: C.warmWhite, marginBottom: "16px", lineHeight: 1.2 }}>
                Dental Costs in Broadmeadows —<br />Honest Prices, No Surprises
              </h1>
              <p style={{ color: "rgba(250,250,247,0.82)", fontSize: "1.05rem", maxWidth: "620px", margin: "0 auto 36px" }}>
                We believe you deserve to know the cost before you sit in the chair. Clear pricing.
                Flexible payments. Bulk billing for eligible children.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(185px, 1fr))", gap: "10px", maxWidth: "720px", margin: "0 auto 40px" }}>
                <TrustBadge icon={<Check size={16} />} label="CDBS Bulk Billing Available" />
                <TrustBadge icon={<CreditCard size={16} />} label="Private Health HICAPS Claiming" />
                <TrustBadge icon={<Zap size={16} />} label="Flexible Payment Plans" />
                <TrustBadge icon={<Heart size={16} />} label="No-Pressure Consultations" />
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", justifyContent: "center", marginBottom: "20px" }}>
                <a href="#book" style={tealBtn("lg")} onClick={() => handleCtaClick("Submit Your Details to Get a Call Back", "hero")}>
                  <Calendar size={18} />Submit Your Details to Get a Call Back
                </a>
                <a href="tel:0390224442" style={outlineBtn("lg")} onClick={() => handlePhoneTap("hero")}>
                  <Phone size={18} />Call 03 9022 4442
                </a>
              </div>
              <p style={{ color: "rgba(250,250,247,0.5)", fontSize: "0.8rem" }}>
                No commitment required. We'll explain all costs before any treatment begins.
              </p>
            </div>
          </div>
        </section>

        {/* APPROACH STRIP */}
        <section style={{ background: C.warmGrey, ...pad }}>
          <div style={wrap}>
            <h2 style={{ ...serifHeading, fontSize: "clamp(1.4rem, 4vw, 2rem)", color: C.navy, textAlign: "center", marginBottom: "48px" }}>
              Our Commitment to Cost Transparency
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "24px" }}>
              {[
                { icon: <Shield size={28} color={C.teal} />, heading: "You're Assessed First, Not Charged First", copy: "Every treatment is individually assessed by a registered dentist before any cost is confirmed." },
                { icon: <FileText size={28} color={C.teal} />, heading: "Your Treatment Plan, Before Any Work", copy: "You'll receive a clear, itemised treatment plan before we proceed — no hidden fees, no surprises." },
                { icon: <Heart size={28} color={C.teal} />, heading: "You're Always In Control", copy: "There's no pressure to proceed. You decide what treatment, when, and how you'd like to pay." },
              ].map((p, i) => (
                <div key={i} style={{ background: C.white, borderRadius: "12px", padding: "28px", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
                  <div style={{ marginBottom: "14px" }}>{p.icon}</div>
                  <h3 style={{ ...serifHeading, fontSize: "1.05rem", color: C.navy, marginBottom: "8px" }}>{p.heading}</h3>
                  <p style={{ color: "#555", fontSize: "0.9rem", margin: 0 }}>{p.copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY COSTS VARY */}
        <section style={{ background: C.white, ...pad }}>
          <div style={{ ...wrap, maxWidth: "760px" }}>
            <hr style={{ border: "none", borderTop: `2px solid ${C.teal}`, opacity: 0.25, marginBottom: "48px" }} />
            <h2 style={{ ...serifHeading, fontSize: "clamp(1.4rem, 4vw, 2rem)", color: C.navy, marginBottom: "28px" }}>
              Why Does Dental Care Cost Different Amounts at Different Clinics?
            </h2>
            <div style={{ display: "flex", gap: "36px", flexWrap: "wrap", alignItems: "flex-start" }}>
              <div style={{ flex: "1 1 340px" }}>
                <p style={{ color: "#444", marginBottom: "16px" }}>
                  You've probably noticed that dental prices vary quite a bit from one clinic to another. It's a fair question to ask — and you deserve a straight answer.
                </p>
                <p style={{ color: "#444", marginBottom: "16px" }}>
                  Think of it like choosing where to stay when you travel. A motel gives you a bed and a roof. A five-star hotel gives you a trained concierge, premium linen, attentive staff, and an experience that feels considered at every step. Both are technically "accommodation" — but the level of care, the materials, and how you feel walking out are completely different.
                </p>
                <p style={{ color: "#444", marginBottom: "16px" }}>
                  Dental care works the same way. A clinic optimised for volume and low cost delivers a very different experience from one that invests in digital imaging technology, premium restorative materials, ongoing clinical education, and — most importantly — the time to properly understand you as a patient.
                </p>
                <p style={{ color: "#444", marginBottom: "16px" }}>
                  At Y3 Smiles Dental, we've made a deliberate choice to centre everything around patient experience. That means:
                </p>
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {[
                    "Your dentist takes time to listen and explain before any treatment",
                    "Costs are clear, discussed openly, and never a surprise",
                    "We don't rush appointments — your comfort and understanding matter",
                    "Treatment plans are tailored to your situation, not applied generically",
                    "The environment is calm, modern, and designed to reduce anxiety",
                  ].map((item, i) => (
                    <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "0.9rem", color: "#444", marginBottom: "6px" }}>
                      <Check size={14} color={C.teal} style={{ marginTop: "4px", flexShrink: 0 }} />{item}
                    </li>
                  ))}
                </ul>
                <p style={{ color: "#444", marginTop: "16px" }}>
                  We believe great dental care is about more than fixing teeth. It's about building a relationship with a practice you trust, where you feel respected, informed, and in control of your own health decisions.
                </p>
                <p style={{ color: "#444", marginTop: "12px" }}>
                  That's the experience we're committed to providing for every patient who walks through our door.
                </p>
              </div>
              <div style={{ flex: "0 1 280px" }}>
                <div style={{ borderLeft: `4px solid ${C.teal}`, background: C.warmGrey, borderRadius: "0 12px 12px 0", padding: "24px", marginBottom: "20px" }}>
                  <p style={{ ...serifHeading, fontSize: "1.05rem", color: C.navy, lineHeight: 1.6, fontStyle: "italic", margin: "0 0 12px" }}>
                    "Great dental care is patient-centred — it begins with listening, not drilling."
                  </p>
                  <p style={{ fontSize: "0.8rem", color: "#666", margin: 0, fontWeight: 600 }}>— Dr Fatima Kurnaz, Y3 Smiles Dental</p>
                </div>
                <div style={{ borderRadius: "12px", overflow: "hidden", background: "#f4f2ef", textAlign: "center" as const }}>
                  <img
                    src="https://y3smilesdental.com.au/DR%20Fatima%20Headshot%20New.png?v=20260421"
                    alt="Dr Fatima Kurnaz, Principal Dentist at Y3 Smiles Dental Broadmeadows"
                    style={{ width: "100%", maxWidth: "320px", display: "block", margin: "0 auto", borderRadius: "12px 12px 0 0" }}
                    loading="lazy"
                    decoding="async"
                  />
                  <p style={{ fontSize: "0.8rem", color: "#666", padding: "10px 12px 14px", margin: 0, fontWeight: 500 }}>Dr Fatima Kurnaz — Principal Dentist &amp; Clinic Director</p>
                </div>
              </div>
            </div>
            <hr style={{ border: "none", borderTop: `2px solid ${C.teal}`, opacity: 0.25, marginTop: "48px" }} />
          </div>
        </section>

        {/* PRICE GUIDE */}
        <section id="price-guide" style={{ background: C.warmGrey, ...pad }}>
          <div style={wrap}>
            <div style={{ textAlign: "center", marginBottom: "40px" }}>
              <h2 style={{ ...serifHeading, fontSize: "clamp(1.4rem, 4vw, 2rem)", color: C.navy, marginBottom: "10px" }}>
                Price Guide – Common Dental Treatments
              </h2>
              <p style={{ color: "#666", fontSize: "0.875rem", fontStyle: "italic" }}>
                A general guide only — your exact cost is confirmed after assessment.
              </p>
            </div>
            <div role="tablist" aria-label="Treatment categories" style={{ display: "flex", gap: "8px", overflowX: "auto", paddingBottom: "4px", marginBottom: "32px" }}>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  role="tab"
                  aria-selected={activeCategory === cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  style={{
                    whiteSpace: "nowrap" as const,
                    padding: "8px 18px",
                    borderRadius: "999px",
                    border: `2px solid ${activeCategory === cat.id ? C.navy : "rgba(26,26,26,0.15)"}`,
                    background: activeCategory === cat.id ? C.navy : "transparent",
                    color: activeCategory === cat.id ? C.white : C.navy,
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            <div role="tabpanel" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))", gap: "16px" }}>
              {activeItems.map((item, i) => (
                <PriceCard key={i} item={item} onBookClick={handleBookClick} />
              ))}
            </div>
          </div>
        </section>

        {/* BOOKING */}
        <section id="book" style={{ background: C.white, ...pad }}>
          <div style={wrap}>
            <div style={{ textAlign: "center", marginBottom: "40px" }}>
              <h2 style={{ ...serifHeading, fontSize: "clamp(1.4rem, 4vw, 2rem)", color: C.navy, marginBottom: "12px" }}>
                Ready to Book? Let's Find a Time That Works for You.
              </h2>
              <p style={{ color: "#555", fontSize: "1rem" }}>
                Book online in under 2 minutes. We'll confirm your appointment and send you all the details by email.
              </p>
            </div>
            <div ref={formRef} style={{ background: C.warmGrey, borderRadius: "16px", padding: "36px", maxWidth: "640px", margin: "0 auto", boxShadow: "0 4px 24px rgba(11,31,58,0.08)" }}>
              {selectedTreatment && (
                <div style={{ background: `${C.teal}18`, border: `1px solid ${C.teal}44`, borderRadius: "8px", padding: "10px 14px", marginBottom: "20px", fontSize: "0.875rem", color: C.navy, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span><strong>Treatment interest:</strong> {selectedTreatment}</span>
                  <button onClick={() => setSelectedTreatment("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#888", fontSize: "0.8rem", marginLeft: "8px" }} aria-label="Clear treatment selection">✕</button>
                </div>
              )}
              <BookingLeadForm treatmentInterest={selectedTreatment || undefined} className="w-full" />
              <p style={{ textAlign: "center", color: "#888", fontSize: "0.78rem", marginTop: "16px" }}>
                We'll send a confirmation to your email once your booking is received. Prefer to talk?{" "}
                <a href="tel:0390224442" style={{ color: C.teal, fontWeight: 600 }} onClick={() => handlePhoneTap("booking_section")}>Call 03 9022 4442</a>
              </p>
            </div>
          </div>
        </section>

        {/* PAYMENT OPTIONS */}
        <section style={{ background: C.navy, ...pad }}>
          <div style={wrap}>
            <h2 style={{ ...serifHeading, fontSize: "clamp(1.4rem, 4vw, 2rem)", color: C.warmWhite, textAlign: "center", marginBottom: "12px" }}>
              Flexible Payment Options — Because Health Shouldn't Wait
            </h2>
            <p style={{ color: "rgba(250,250,247,0.65)", textAlign: "center", marginBottom: "44px" }}>
              We offer multiple ways to manage the cost of your care.
            </p>
            <p style={{ textAlign: "center", marginBottom: "32px" }}>
              <Link to="/patients/payment-options" style={{ color: C.teal, fontWeight: 600, textDecoration: "underline" }}>
                View our full payment options page
              </Link>
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
              {[
                { icon: <Shield size={24} color={C.teal} />, title: "Private Health Insurance (HICAPS)", body: "On-the-spot claiming at reception. We accept all major funds." },
                { icon: <Baby size={24} color={C.teal} />, title: "Medicare CDBS — Kids Bulk Billing", body: "Eligible children aged 1–17 may receive free dental care. We'll check your child's eligibility at no charge." },
                { icon: <CreditCard size={24} color={C.teal} />, title: "Flexible Payment Plans", body: "Spread the cost of treatment over time. Ask our team for options that suit your budget." },
                { icon: <Zap size={24} color={C.teal} />, title: "Pay-Forward Options", body: "Prepay for upcoming treatments at today's prices — ideal for families planning ahead." },
                { icon: <FileText size={24} color={C.teal} />, title: "Clear Written Treatment Plans", body: "Before any work begins, you'll receive a full written estimate so you can plan with confidence." },
                { icon: <Check size={24} color={C.teal} />, title: "No-Surprise Billing", body: "Costs are agreed upfront. We never add fees without your approval." },
              ].map((opt, i) => (
                <div key={i} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", padding: "24px" }}>
                  <div style={{ marginBottom: "12px" }}>{opt.icon}</div>
                  <h3 style={{ ...serifHeading, fontSize: "1rem", color: C.warmWhite, marginBottom: "8px" }}>{opt.title}</h3>
                  <p style={{ color: "rgba(250,250,247,0.7)", fontSize: "0.875rem", margin: 0 }}>{opt.body}</p>
                </div>
              ))}
            </div>
            <div style={{ textAlign: "center", marginTop: "40px" }}>
              <a href="tel:0390224442" style={tealBtn("lg")} onClick={() => handlePhoneTap("payment_section")}>
                <Phone size={18} />Talk to Us About Payment Options — 03 9022 4442
              </a>
            </div>
          </div>
        </section>

        {/* SOCIAL PROOF */}
        <section style={{ background: C.warmGrey, ...pad }}>
          <div style={wrap}>
            <div style={{ textAlign: "center", marginBottom: "40px" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                {[...Array(5)].map((_, i) => <Star key={i} size={24} fill={C.amber} color={C.amber} />)}
                <span style={{ ...serifHeading, fontSize: "1.5rem", color: C.navy }}>5/5</span>
              </div>
              <p style={{ color: "#666", fontSize: "0.85rem" }}>Based on Google Reviews — Y3 Smiles Dental Broadmeadows</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "16px", marginBottom: "60px" }}>
              {[
                { name: "Zara H.", ago: "2 months ago", text: "Amazing care and services, very professional, so very happy with the services and treatment I got. I would highly recommend Y3 Smiles Dental." },
                { name: "Ahmed M.", ago: "3 months ago", text: "The team was fantastic with my kids — explained everything clearly and made them feel at ease. CDBS made it completely free for us. Brilliant." },
                { name: "Maria T.", ago: "1 month ago", text: "Finally found a dentist who gives me a proper estimate before starting. No surprises on the bill. The whole team is warm and professional." },
              ].map((rev, i) => (
                <div key={i} style={{ background: C.white, borderRadius: "12px", padding: "24px", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
                  <div style={{ display: "flex", gap: "4px", marginBottom: "10px" }}>
                    {[...Array(5)].map((_, si) => <Star key={si} size={14} fill={C.amber} color={C.amber} />)}
                  </div>
                  <p style={{ color: "#333", fontSize: "0.9rem", lineHeight: 1.6, marginBottom: "14px" }}>"{rev.text}"</p>
                  <p style={{ fontSize: "0.8rem", color: "#888", fontWeight: 600 }}>{rev.name} · {rev.ago}</p>
                </div>
              ))}
            </div>

            <div id="clinic-gallery" style={{ marginBottom: "60px" }}>
              <h2 style={{ ...serifHeading, fontSize: "1.5rem", color: C.navy, marginBottom: "8px", textAlign: "center" }}>Step Inside Our Clinic</h2>
              <p style={{ color: "#666", fontSize: "0.875rem", textAlign: "center", marginBottom: "28px" }}>Modern, calm, and designed with your comfort in mind — from the moment you walk in.</p>
              <div style={{ columns: "3 200px", gap: "12px", columnFill: "balance" }}>
                {[
                  { src: "https://y3smilesdental.com.au/clinic-pics/large/Y3%20-%20Photo-6.jpg", alt: "Y3 Smiles Dental Broadmeadows clinic interior" },
                  { src: "https://y3smilesdental.com.au/clinic-pics/large/Y3%20-%20Photo-18.jpg", alt: "Y3 Smiles Dental reception area Broadmeadows" },
                  { src: "https://y3smilesdental.com.au/clinic-pics/large/Y3%20-%20Photo-22.jpg", alt: "Y3 Smiles Dental treatment room" },
                  { src: "https://y3smilesdental.com.au/clinic-pics/large/Y3%20-%20Photo-45.jpg", alt: "Y3 Smiles Dental clinic Broadmeadows" },
                  { src: "https://y3smilesdental.com.au/clinic-pics/large/Y3%20-%20Photo-52.jpg", alt: "Y3 Smiles Dental dental chair and equipment" },
                  { src: "https://y3smilesdental.com.au/clinic-pics/large/Y3%20-%20Photo-56.jpg", alt: "Y3 Smiles Dental modern clinic interior" },
                  { src: "https://y3smilesdental.com.au/clinic-pics/large/Y3%20-%20Photo-63.jpg", alt: "Y3 Smiles Dental Broadmeadows waiting area" },
                  { src: "https://y3smilesdental.com.au/clinic-pics/large/Y3%20-%20Photo-70.jpg", alt: "Y3 Smiles Dental treatment room equipment" },
                  { src: "https://y3smilesdental.com.au/clinic-pics/large/Y3%20-%20Photo-80.jpg", alt: "Y3 Smiles Dental clinic Broadmeadows" },
                ].map((photo, i) => (
                  <figure key={i} style={{ margin: "0 0 12px", breakInside: "avoid", display: "block" }}>
                    <div style={{ borderRadius: "10px", overflow: "hidden", background: "#e8e5e0" }}>
                      <img
                        src={photo.src}
                        alt={photo.alt}
                        style={{ width: "100%", display: "block", transition: "transform 0.3s ease" }}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  </figure>
                ))}
              </div>
              <div style={{ textAlign: "center", marginTop: "20px" }}>
                <a href="https://y3smilesdental.com.au/about/clinic-photos" style={{ color: C.teal, fontWeight: 600, fontSize: "0.9rem", textDecoration: "none" }}>
                  → See all clinic photos
                </a>
              </div>
            </div>

            <div style={{ textAlign: "center" }}>
              <h2 style={{ ...serifHeading, fontSize: "1.5rem", color: C.navy, marginBottom: "6px" }}>Follow Us on Instagram</h2>
              <p style={{ color: "#666", fontSize: "0.875rem", marginBottom: "24px" }}>@y3smilesdental — behind the scenes, patient education, and more</p>
              <div style={{ display: "flex", gap: "10px", paddingBottom: "4px", justifyContent: "center", flexWrap: "wrap" }}>
                {["https://www.instagram.com/reel/DRlpdi4kt2R/", "https://www.instagram.com/p/DRddZIHjyLd/", "https://www.instagram.com/p/DQ_H2Ctj49o/"].map((url, i) => (
                  <a
                    key={i}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => { if (typeof window !== "undefined" && (window as any).dataLayer) { (window as any).dataLayer.push({ event: "instagram_follow_click" }); } }}
                    aria-label="View post on Instagram"
                    style={{ display: "flex", flexShrink: 0, width: "110px", height: "110px", borderRadius: "8px", overflow: "hidden", background: "linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)", alignItems: "center", justifyContent: "center" }}
                  >
                    <Instagram size={36} color="white" />
                  </a>
                ))}
              </div>
              <div style={{ marginTop: "20px" }}>
                <a
                  href="https://www.instagram.com/y3smilesdental/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", gap: "8px", color: C.navy, fontWeight: 700, fontSize: "0.9rem", textDecoration: "none" }}
                  onClick={() => { if (typeof window !== "undefined" && (window as any).dataLayer) { (window as any).dataLayer.push({ event: "instagram_follow_click" }); } }}
                >
                  <Instagram size={18} />Follow @y3smilesdental ↗
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* CDBS CALLOUT */}
        <section id="cdbs" style={{ background: C.navy, ...pad }}>
          <div style={wrap}>
            <div style={{ textAlign: "center", marginBottom: "36px" }}>
              <Baby size={40} color={C.white} style={{ marginBottom: "16px" }} />
              <h2 style={{ ...serifHeading, fontSize: "clamp(1.4rem, 4vw, 2rem)", color: C.white, marginBottom: "20px" }}>
                Is Your Child Eligible for Free Dental Care?
              </h2>
              <p style={{ color: "rgba(255,255,255,0.88)", fontSize: "1rem", maxWidth: "640px", margin: "0 auto 28px", lineHeight: 1.7 }}>
                Through Medicare's Child Dental Benefits Schedule (CDBS), eligible children aged 1 to 17 may receive up to{" "}
                <strong>$1,158 in free dental care</strong> over two calendar years. This includes check-ups, cleans, fillings, X-rays, and extractions.
                <br /><br />
                Y3 Smiles Dental is an approved CDBS provider. We'll check your child's eligibility at no charge — just bring your Medicare card.
              </p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px", maxWidth: "640px", margin: "0 auto 36px" }}>
              {["Check-ups & X-rays", "Professional cleaning", "Tooth-coloured fillings", "Extractions", "Fissure sealants", "Root canal (anterior teeth)"].map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", color: C.white, fontSize: "0.875rem", fontWeight: 500 }}>
                  <Check size={16} color={C.white} />{item}
                </div>
              ))}
            </div>
            <div style={{ textAlign: "center" }}>
              <a href="#book" style={{ ...tealBtn("lg"), background: C.navy }} onClick={() => handleCtaClick("Check My Child's CDBS Eligibility", "cdbs_section")}>
                <Calendar size={18} />Check My Child's CDBS Eligibility →
              </a>
              <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.75rem", marginTop: "14px" }}>
                CDBS eligibility is determined by Medicare. Conditions apply. Ask our team to check at no cost.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section style={{ background: C.white, ...pad }}>
          <div style={{ ...wrap, maxWidth: "760px" }}>
            <h2 style={{ ...serifHeading, fontSize: "clamp(1.4rem, 4vw, 2rem)", color: C.navy, marginBottom: "40px", textAlign: "center" }}>
              Your Questions About Dental Costs — Answered
            </h2>
            <FaqItem q="Do I need to pay upfront?" a="No. We provide a written cost estimate before any treatment begins. You decide whether and when to proceed." />
            <FaqItem q="Can I claim on my private health insurance?" a="Yes. We have HICAPS on-site, so you can claim on the spot. The amount covered depends on your fund and level of cover." />
            <FaqItem q="My child qualifies for CDBS — what does that mean?" a="It means certain dental treatments for your child may be fully covered by Medicare at no cost to you. We'll check eligibility at your first visit. Just bring your Medicare card." />
            <FaqItem q="Are the prices on this page final?" a="The prices listed are a guide only. Your exact cost will be confirmed by your dentist after a clinical assessment, before any treatment begins." />
            <FaqItem q="What if I can't afford treatment all at once?" a="We offer flexible payment plans and pay-forward options to spread the cost. Speak to our reception team and we'll find an option that works for you." />
            <FaqItem q="Do you charge for a consultation?" a={<>A limited examination starts from $65. For a full new patient check-up and clean (which includes X-rays if needed, photos, and professional cleaning), fees start from $229. Many private health patients may have <strong>$0 out-of-pocket</strong>.</>} />
            <FaqItem q="Is the $0 new patient check-up genuine?" a="The $0 out-of-pocket outcome depends on your specific private health cover. We can give you an estimate when you call or book — just let us know your fund." />
            <FaqItem q="Do you offer payment plans for implants or orthodontics?" a="Yes. For larger treatments like implants and clear aligners, we can discuss payment plan options at your consultation." />
          </div>
        </section>

        {/* FINAL CTA */}
        <section style={{ background: C.navy, ...pad }}>
          <div style={{ ...wrap, textAlign: "center" }}>
            <h2 style={{ ...serifHeading, fontSize: "clamp(1.4rem, 4vw, 2.2rem)", color: C.warmWhite, marginBottom: "16px" }}>
              Ready to Know Exactly What Your Dental Care Will Cost?
            </h2>
            <p style={{ color: "rgba(250,250,247,0.75)", fontSize: "1rem", maxWidth: "560px", margin: "0 auto 36px" }}>
              Book a personalised consultation. We'll assess your oral health, explain your options, and give you a clear written cost plan — with no pressure to proceed.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "14px", justifyContent: "center", marginBottom: "32px" }}>
              <a href="#book" style={tealBtn("lg")} onClick={() => handleCtaClick("Book Online Now", "final_cta")}>
                <Calendar size={18} />Book Online Now →
              </a>
              <a href="tel:0390224442" style={outlineBtn("lg")} onClick={() => handlePhoneTap("final_cta")}>
                <Phone size={18} />Call 03 9022 4442
              </a>
            </div>
            <p style={{ color: "rgba(250,250,247,0.5)", fontSize: "0.8rem" }}>
              Mon–Fri: 9:00 AM – 5:30 PM &nbsp;|&nbsp; Saturday: 9:00 AM – 1:00 PM &nbsp;|&nbsp; Sunday: Closed
            </p>
          </div>
        </section>

        {/* AHPRA Disclaimer */}
        <div style={{ background: C.warmGrey, borderTop: "1px solid rgba(11,31,58,0.08)", padding: "20px", textAlign: "center" }}>
          <p style={{ fontSize: "0.75rem", color: "#999", maxWidth: "760px", margin: "0 auto", lineHeight: 1.6 }}>
            This is general information only and not dental advice. All prices are a guide only and are subject to clinical assessment by a registered dentist before treatment commences. CDBS eligibility is determined by Medicare and conditions apply. Y3 Smiles Dental is an AHPRA-registered dental practice. ABN: 49 137 405 383.
          </p>
        </div>
      </main>

      <Footer />

      {/* STICKY MOBILE BAR */}
      {showStickyBar && (
        <div className="lg:hidden" style={{ position: "fixed", bottom: 0, left: 0, right: 0, display: "flex", zIndex: 100 }} aria-label="Quick contact bar">
          <a href="tel:0390224442" onClick={() => handlePhoneTap("sticky_bar")} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", background: C.navy, color: C.white, padding: "16px", fontSize: "0.9rem", fontWeight: 700, textDecoration: "none", minHeight: "56px" }}>
            <Phone size={18} />Call Now
          </a>
          <a href="#book" onClick={() => handleCtaClick("Book Online", "sticky_bar")} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", background: C.teal, color: C.white, padding: "16px", fontSize: "0.9rem", fontWeight: 700, textDecoration: "none", minHeight: "56px" }}>
            <Calendar size={18} />Book Online
          </a>
        </div>
      )}

      <style>{`
        *:focus-visible { outline: 2px solid #B59682; outline-offset: 2px; }
        @media (min-width: 1024px) { .lg\\:hidden { display: none !important; } }
      `}</style>
    </div>
  );
};

export default PricingPage;